import { getEnv } from "@istari-frontend/shared";
import { WebStorageStateStore } from "oidc-client-ts";

const LEGACY_VARS = [
  "VITE_IDENTITY_ROUTER_ENABLED",
  "VITE_IDENTITY_ROUTER_AUTHORITY",
  "VITE_IDENTITY_ROUTER_CLIENT_ID",
];
const NEW_VARS = [
  "VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED",
  "VITE_ISTARI_DIGITAL_API_URL",
  "VITE_IDENTITY_SERVICE_CLIENT_ID",
];

// Mirrors src/utils/identity-service.ts's own copy. Derived inline because libs/auth cannot
// import from src/, so each module tracks its own once-per-load warning independently.
let warned = false;

function warnIfDeprecatedVarsInUse() {
  if (warned) return;
  const anyLegacy = LEGACY_VARS.some((name) => getEnv(name) !== undefined);
  if (!anyLegacy) return;
  warned = true;
  const anyNew = NEW_VARS.some((name) => getEnv(name) !== undefined);
  console.info(
    anyNew
      ? "VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED / VITE_ISTARI_DIGITAL_API_URL / VITE_IDENTITY_SERVICE_CLIENT_ID take precedence over the deprecated VITE_IDENTITY_ROUTER_* variables, which are ignored."
      : "VITE_IDENTITY_ROUTER_* environment variables are deprecated and in use; migrate to VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED, VITE_ISTARI_DIGITAL_API_URL, and VITE_IDENTITY_SERVICE_CLIENT_ID.",
  );
}

const BASE_SCOPE =
  "openid profile email offline_access urn:zitadel:iam:org:project:id:zitadel:aud urn:zitadel:iam:user:resourceowner";

/**
 * `enabled`, `authority`, and `client_id` each fall back to the deprecated
 * `VITE_IDENTITY_ROUTER_*` names independently when the new name is unset. The
 * gateway-URL invariant only applies when `enabled` came from the new toggle var —
 * legacy config carries its own explicit authority and never had a gateway URL.
 */
export function buildAuthConfig() {
  warnIfDeprecatedVarsInUse();

  const newEnabledRaw = getEnv("VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED");
  const usedLegacyEnabled = newEnabledRaw === undefined;
  const enabledRaw = usedLegacyEnabled ? getEnv("VITE_IDENTITY_ROUTER_ENABLED") : newEnabledRaw;
  const identityServiceEnabled = enabledRaw?.toLowerCase() === "true";

  // Trim before stripping slashes: the $ anchor cannot reach past trailing whitespace.
  const digitalApiUrl = getEnv("VITE_ISTARI_DIGITAL_API_URL")?.trim().replace(/\/+$/, "") || undefined;

  if (identityServiceEnabled && !usedLegacyEnabled && !digitalApiUrl) {
    throw new Error(
      "VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED is true but VITE_ISTARI_DIGITAL_API_URL is unset or empty after trimming; identity-service mode requires the gateway base URL.",
    );
  }

  const authority = identityServiceEnabled
    ? (digitalApiUrl && `${digitalApiUrl}/identity`) || getEnv("VITE_IDENTITY_ROUTER_AUTHORITY")
    : getEnv("VITE_ZITADEL_AUTHORITY");

  const clientId = identityServiceEnabled
    ? (getEnv("VITE_IDENTITY_SERVICE_CLIENT_ID") ?? getEnv("VITE_IDENTITY_ROUTER_CLIENT_ID"))
    : getEnv("VITE_ZITADEL_CLIENT_ID");

  return {
    userStore: new WebStorageStateStore({
      store: window.localStorage,
    }),
    authority,
    client_id: clientId,
    response_type: "code",
    scope: identityServiceEnabled ? `${BASE_SCOPE} sec` : BASE_SCOPE,
    post_logout_redirect_uri: getEnv("VITE_LOGOUT_REDIRECT_URI") ?? getEnv("VITE_UI_URL"),
    redirect_uri: getEnv("VITE_REDIRECT_URI") ?? getEnv("VITE_UI_URL"),
  };
}

export const authConfig = buildAuthConfig();
